import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ChipModule } from 'primeng/chip';
import { ChipsModule } from 'primeng/chips';
import { ButtonModule } from 'primeng/button';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { LocalizedConfigOption, RuntimeSettings, } from '../../../core/models/runtime-settings';
import { ConfigService } from '../../../core/services/config.service';
import { CountryService } from '../../../core/services/country.service';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { MessageService } from 'primeng/api';
import { RegexConstants } from '../../../core/constants/regex-constants';
import { ErrorMessages } from '../../../core/constants/error-messages';
import { InputErrorMessageComponent } from '../../../shared/components/input-error-message/input-error-message.component';
import { RuntimeSettingsService } from '../../../core/services/runtime-settings.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { PermissionService } from '../../../core/services/permission.service';

const listItemsValidator =
  (pattern: RegExp, min = 3, max = 30): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const items = control.value as string[] | null;
    if (!items?.length) return null;
    const invalid = items.some((item) => {
      const v = (item ?? '').trim();
      return v.length < min || v.length > max || !pattern.test(v);
    });
    return invalid ? { invalidListItem: true } : null;
  };

const corporateEmailListValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const emails = control.value as string[] | null;
  if (!emails?.length) {
    return null;
  }

  const valid = emails.every((email) => /^[^\s@]+@emeal\.nttdata\.com$/i.test(email.trim()));
  return valid ? null : { corporateEmailDomain: true };
};

@Component({
  selector: 'em-runtime-settings',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    ReactiveFormsModule,
    ChipModule,
    ButtonModule,
    ChipsModule,
    DropdownModule,
    MultiSelectModule,
    InputErrorMessageComponent,
    TranslatePipe,
    EmPermissionDirective,
  ],
  templateUrl: './runtime-settings.component.html',
  styleUrl: './runtime-settings.component.scss',
})
export class RuntimeSettingsComponent {
  runtimeSettings: RuntimeSettings;
  runtimeSettingsForm: FormGroup;
  afmaTypeForm: FormGroup;
  medicalVisitTypeForm: FormGroup;
  countries: string[] = [];
  errorMessages = ErrorMessages.runtimeSettings;
  translations: any = {};
  Permission = Permission;
  canWrite = false;
  globalEmploymentTypeOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private configService: ConfigService,
    private countryService: CountryService,
    private runtimeSettingsService: RuntimeSettingsService,
    private messageService: MessageService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    
  ) {
    this.runtimeSettings = this.initializeRunTimeSettings();
    this.runtimeSettingsForm = this.createForm();
    this.afmaTypeForm = this.createLocalizedOptionForm();
    this.medicalVisitTypeForm = this.createLocalizedOptionForm();
    this.canWrite = this.permissionService.hasPermission(Permission.CONFIG_BSA);
   if (!this.canWrite) {
      this.runtimeSettingsForm.disable({ emitEvent: false });
      this.afmaTypeForm.disable({ emitEvent: false });
      this.medicalVisitTypeForm.disable({ emitEvent: false });
    }
  }

  ngOnInit(): void {
    this.loadCountries();
    this.loadRuntimeSettings();
    this.loadTranslations();
    this.buildGlobalEmploymentTypeOptions();
    this.translate.onLangChange.subscribe(() => this.buildGlobalEmploymentTypeOptions());
    this.syncContractNamesWithSourceFields();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.runtimeSettings.successMessage.title',
        'layout.configurations.runtimeSettings.successMessage.detail',
        'layout.configurations.runtimeSettings.errorMessage.title',
        'layout.configurations.runtimeSettings.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  private buildGlobalEmploymentTypeOptions(): void {
    this.globalEmploymentTypeOptions = [
      {
        label: this.translate.instant('layout.configurations.runtimeSettings.employmentTypes.employee'),
        value: 'EMPLOYEE',
      },
      {
        label: this.translate.instant('layout.configurations.runtimeSettings.employmentTypes.freelance'),
        value: 'FREELANCE',
      },
      {
        label: this.translate.instant('layout.configurations.runtimeSettings.employmentTypes.temporary'),
        value: 'TEMPORARY',
      },
    ];
  }

  private initializeRunTimeSettings(): RuntimeSettings {
    return {
      socialSecurityProviderName: '',
      secondaryPensionProvider: '',
      medicalInsuranceProvider: '',
      academicalDegrees: [],
      highSchoolDiplomaName: [],
      contractDescription: '',
      location: '',
      appType: '',
      accountNumberLabel: '',
      accountNumberPattern: '',
      lastPromotionPeriod: 0,
      probationPeriodMonths: 0,
      entryLevelContractName: [],
      permanentContractName: [],
      subContractorContractName: [],
      secondaryPensionRatio: [],
      languageNames: [],
      languageLevels: [],
      contractNames: [],
      activeSections: [],
      emailsNotificationList: [],
      dataRetentionYears: 0,
      worklogGenerationMonth: 9,
      educationGrantMinAge: 3,
      educationGrantMaxAge: 16,
      globalEmploymentType: [],
      contractTerminationCauses: [],
      afmaTypes: [],
      medicalVisitTypes: [],
      medicalVisitAuditMonths: 3,
    };
  }

  private createForm(): FormGroup {
    return this.fb.group({
      socialSecurityProviderName: [
        '',
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.LETTERS_AND_SPACES),
            Validators.minLength(3),
            Validators.maxLength(30),
          ]),
        },
      ],
      secondaryPensionProvider: [
        '',
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.LETTERS_AND_SPACES),
            Validators.minLength(3),
            Validators.maxLength(30),
          ]),
        },
      ],
      medicalInsuranceProvider: [
        '',
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.ALPHANUMERIC_AND_SPACES),
            Validators.minLength(3),
            Validators.maxLength(30),
          ]),
        },
      ],
      academicalDegrees: [[], listItemsValidator(RegexConstants.ALPHANUMERIC_PLUS_MINUS)],
      highSchoolDiplomaName: [[], listItemsValidator(RegexConstants.ALPHANUMERIC_PLUS_MINUS)],
      contractDescription: [
        '',
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.ALPHANUMERIC_SPACES_HYPHEN),
            Validators.minLength(3),
            Validators.maxLength(50),
          ]),
        },
      ],
      location: [''],
      appType: [
        '',
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.ALPHANUMERIC_AND_SPACES),
            Validators.minLength(3),
            Validators.maxLength(30),
          ]),
        },
      ],
      accountNumberLabel: [
        '',
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.ALPHANUMERIC_AND_SPACES),
            Validators.minLength(3),
            Validators.maxLength(30),
          ]),
        },
      ],
      accountNumberPattern: [
        '',
        {
          validators: Validators.compose([Validators.minLength(1), Validators.maxLength(100)]),
        },
      ],
      lastPromotionPeriod: [
        0,
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.ONLY_NUMBERS_REGEX),
            Validators.maxLength(3),
          ]),
        },
      ],
      probationPeriodMonths: [
        0,
        {
          validators: Validators.compose([
            Validators.pattern(RegexConstants.ONLY_NUMBERS_REGEX),
            Validators.maxLength(2),
          ]),
        },
      ],
      entryLevelContractName: [[]],
      permanentContractName: [[]],
      subContractorContractName: [[]],
      secondaryPensionRatio: [[]],
      languageNames: [[]],
      languageLevels: [[]],
      contractNames: [[]],
      activeSections: [[]],
      emailsNotificationList: [[], corporateEmailListValidator],
      dataRetentionYears: [0],
      worklogGenerationMonth: [9, [Validators.min(1), Validators.max(12)]],
      educationGrantMinAge: [3, [Validators.required, Validators.min(0), Validators.max(99)]],
      educationGrantMaxAge: [16, [Validators.required, Validators.min(0), Validators.max(99)]],
      globalEmploymentType: [[]],
      contractTerminationCauses: [[]],
      afmaTypes: [[]],
      medicalVisitTypes: [[]],
      medicalVisitAuditMonths: [3, [Validators.min(0), Validators.max(60)]],
    });
  }
private createLocalizedOptionForm(): FormGroup {
  return this.fb.group({
    code: ['', Validators.required],
    labelEn: ['', Validators.required],
    labelEs: ['', Validators.required],
    labelAr: ['', Validators.required],
  });
}
  private updateForm(runtimeSettings: RuntimeSettings) {
    this.runtimeSettingsForm.patchValue({
      socialSecurityProviderName: runtimeSettings.socialSecurityProviderName,
      secondaryPensionProvider: runtimeSettings.secondaryPensionProvider,
      medicalInsuranceProvider: runtimeSettings.medicalInsuranceProvider,
      academicalDegrees: this.toList(runtimeSettings.academicalDegrees),
      highSchoolDiplomaName: this.toList(runtimeSettings.highSchoolDiplomaName),
      contractDescription: runtimeSettings.contractDescription,
      location: runtimeSettings.location,
      appType: runtimeSettings.appType,
      accountNumberLabel: runtimeSettings.accountNumberLabel,
      accountNumberPattern: runtimeSettings.accountNumberPattern,
      lastPromotionPeriod: runtimeSettings.lastPromotionPeriod,
      probationPeriodMonths: runtimeSettings.probationPeriodMonths,
      entryLevelContractName: runtimeSettings.entryLevelContractName,
      permanentContractName: runtimeSettings.permanentContractName,
      subContractorContractName: runtimeSettings.subContractorContractName,
      secondaryPensionRatio: runtimeSettings.secondaryPensionRatio,
      languageNames: runtimeSettings.languageNames,
      languageLevels: runtimeSettings.languageLevels,
      contractNames: runtimeSettings.contractNames,
      activeSections: runtimeSettings.activeSections,
      emailsNotificationList: runtimeSettings.emailsNotificationList,
      dataRetentionYears: runtimeSettings.dataRetentionYears,
      worklogGenerationMonth: runtimeSettings.worklogGenerationMonth ?? 9,
      educationGrantMinAge: runtimeSettings.educationGrantMinAge ?? 3,
      educationGrantMaxAge: runtimeSettings.educationGrantMaxAge ?? 16,
      globalEmploymentType: runtimeSettings.globalEmploymentType ?? [],
      contractTerminationCauses: runtimeSettings.contractTerminationCauses ?? [],
      afmaTypes: runtimeSettings.afmaTypes ?? [],
      medicalVisitTypes: runtimeSettings.medicalVisitTypes ?? [],
      medicalVisitAuditMonths: runtimeSettings.medicalVisitAuditMonths ?? 3,
    });
  }
  private toList(value: string | string[] | null | undefined): string[] {
  if (Array.isArray(value)) return value;
  return value?.trim() ? [value.trim()] : [];
}

  private loadRuntimeSettings() {
    this.configService.getRuntimeSettings().subscribe((res) => {
      this.runtimeSettings = res;
      this.updateForm(this.runtimeSettings);
    });
  }

  private syncContractNamesWithSourceFields(): void {
    const sourceFields = ['entryLevelContractName', 'permanentContractName', 'subContractorContractName'];
    sourceFields.forEach((fieldName) => {
      this.runtimeSettingsForm.get(fieldName)?.valueChanges.subscribe(() => {
        this.rebuildContractNames();
      });
    });
  }

  rebuildContractNames(): void {
    const entryLevelContractName = this.runtimeSettingsForm.get('entryLevelContractName')?.value || [];
    const permanentContractName = this.runtimeSettingsForm.get('permanentContractName')?.value || [];
    const subContractorContractName = this.runtimeSettingsForm.get('subContractorContractName')?.value || [];

    this.runtimeSettingsForm.patchValue(
      {
        contractNames: [
          ...entryLevelContractName,
          ...permanentContractName,
          ...subContractorContractName,
        ],
      },
      { emitEvent: false },
    );
  }

  private loadCountries() {
    this.countryService
      .getCountries()
      .subscribe((countries) => (this.countries = countries.map((country) => country.name.toUpperCase())));
  }

  removeContractName(contractName: string): void {
    const normalizedContractName = this.normalizeContractName(contractName);

    this.runtimeSettingsForm.patchValue({
      entryLevelContractName: this.removeContractNameFromList(
        this.runtimeSettingsForm.get('entryLevelContractName')?.value,
        normalizedContractName,
      ),
      permanentContractName: this.removeContractNameFromList(
        this.runtimeSettingsForm.get('permanentContractName')?.value,
        normalizedContractName,
      ),
      subContractorContractName: this.removeContractNameFromList(
        this.runtimeSettingsForm.get('subContractorContractName')?.value,
        normalizedContractName,
      ),
    });
  }

  private removeContractNameFromList(list: string[] = [], normalizedContractName: string): string[] {
    return list.filter((value) => this.normalizeContractName(value) !== normalizedContractName);
  }

  private normalizeContractName(value: string): string {
    return (value || '').trim().toUpperCase();
  }
get afmaTypes(): LocalizedConfigOption[] {
  return this.runtimeSettingsForm.get('afmaTypes')?.value ?? [];
}

get medicalVisitTypes(): LocalizedConfigOption[] {
  return this.runtimeSettingsForm.get('medicalVisitTypes')?.value ?? [];
}

get activeAfmaTypes(): LocalizedConfigOption[] {
  return this.afmaTypes.filter((type) => type.active !== false);
}

get activeMedicalVisitTypes(): LocalizedConfigOption[] {
  return this.medicalVisitTypes.filter((type) => type.active !== false);
}
  onSubmit(): void {
    if (!this.canWrite) {
      return;
    }
    if (this.runtimeSettingsForm.valid) {
      const updatedSettings = this.runtimeSettingsForm.value;
      const payload = { ...updatedSettings } as any;

      payload.contractTerminationCauses = (payload.contractTerminationCauses ?? [])
        .map((cause: string) => this.normalizeConfigCode(cause ?? ''))
        .filter((cause: string) => cause !== '');

      if (Number(payload.educationGrantMinAge) > Number(payload.educationGrantMaxAge)) {
        this.showMessage(
          'error',
          this.translate.instant('layout.configurations.runtimeSettings.errorMessage.title'),
          this.translate.instant('layout.configurations.runtimeSettings.errors.educationGrantAgeRange'),
        );
        return;
      }

      const hasBackendChanges = !this.isRuntimeSettingsEqual(this.runtimeSettings, payload);

      if (!hasBackendChanges) {
        this.showMessage(
          'warn',
          this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
          this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage'),
        );
        return;
      }

      const contractNamesDuplicate = this.findStringDuplicate(updatedSettings.contractNames);
      if (contractNamesDuplicate) {
        this.showMessage(
          'error',
          this.translate.instant('layout.configurations.runtimeSettings.errorMessage.title'),
          this.translate.instant('layout.configurations.runtimeSettings.errors.duplicateContractName', { value: contractNamesDuplicate }),
        );
        return;
      }

      const terminationCauseDuplicate = this.findStringDuplicate(payload.contractTerminationCauses);
      if (terminationCauseDuplicate) {
        this.showMessage(
          'error',
          this.translate.instant('layout.configurations.runtimeSettings.errorMessage.title'),
          this.translate.instant('layout.configurations.runtimeSettings.errors.duplicateTerminationCause', { value: terminationCauseDuplicate }),
        );
        return;
      }

      const degreeDuplicate = this.findStringDuplicate(
        updatedSettings.academicalDegrees,
      );
      if (degreeDuplicate) {
        this.showMessage(
          'error',
          this.translate.instant(
            'layout.configurations.runtimeSettings.errorMessage.title',
          ),
          this.translate.instant(
            'layout.configurations.runtimeSettings.errors.duplicateDegreeType',
            { value: degreeDuplicate },
          ),
        );
        return;
      }

      const levelDuplicate = this.findStringDuplicate(
        updatedSettings.highSchoolDiplomaName,
      );
      if (levelDuplicate) {
        this.showMessage(
          'error',
          this.translate.instant(
            'layout.configurations.runtimeSettings.errorMessage.title',
          ),
          this.translate.instant(
            'layout.configurations.runtimeSettings.errors.duplicateEducationLevel',
            { value: levelDuplicate },
          ),
        );
        return;
      }

      this.configService.updateRuntimeSettings(payload).subscribe({
        next: (updatedSettings) => {
          this.showMessage(
            'success',
            this.translations['layout.configurations.runtimeSettings.successMessage.title'],
            this.translations['layout.configurations.runtimeSettings.successMessage.detail'],
          );
          this.runtimeSettingsService.updateRuntimeSettings(updatedSettings);
        },
        error: (error) => this.handleError(error),
      });
    }
  }
  addAfmaType(): void {
  if (!this.canWrite) {
    return;
  }

  if (this.afmaTypeForm.invalid) {
    this.afmaTypeForm.markAllAsTouched();
    return;
  }

  const option = this.buildLocalizedOption(this.afmaTypeForm.value);

  const currentTypes = [...this.afmaTypes];

  const existingIndex = currentTypes.findIndex(
    (type) => this.normalizeConfigCode(type.code) === option.code,
  );

  if (existingIndex >= 0) {
    if (currentTypes[existingIndex].active === false) {
      currentTypes[existingIndex] = {
        ...option,
        active: true,
      };

      this.runtimeSettingsForm.patchValue({
        afmaTypes: currentTypes,
      });

      this.afmaTypeForm.reset();
      return;
    }

    this.showMessage(
      'warn',
      this.translate.instant(
        'layout.configurations.runtimeSettings.errorMessage.title',
      ),
      this.translate.instant('layout.configurations.runtimeSettings.errors.duplicateAfmaType', { value: option.code }),
    );

    return;
  }

  this.runtimeSettingsForm.patchValue({
    afmaTypes: [...currentTypes, option],
  });

  this.afmaTypeForm.reset();
  }
  removeAfmaType(code: string): void {
  if (!this.canWrite) {
    return;
  }

  const updatedTypes = this.afmaTypes.map((type) =>
    this.normalizeConfigCode(type.code) === this.normalizeConfigCode(code)
      ? {
          ...type,
          active: false,
        }
      : type,
  );

  this.runtimeSettingsForm.patchValue({
    afmaTypes: updatedTypes,
  });
  }
  addMedicalVisitType(): void {
  if (!this.canWrite) {
    return;
  }

  if (this.medicalVisitTypeForm.invalid) {
    this.medicalVisitTypeForm.markAllAsTouched();
    return;
  }

  const option = this.buildLocalizedOption(
    this.medicalVisitTypeForm.value,
  );

  const currentTypes = [...this.medicalVisitTypes];

  const existingIndex = currentTypes.findIndex(
    (type) => this.normalizeConfigCode(type.code) === option.code,
  );

  if (existingIndex >= 0) {
    if (currentTypes[existingIndex].active === false) {
      currentTypes[existingIndex] = {
        ...option,
        active: true,
      };

      this.runtimeSettingsForm.patchValue({
        medicalVisitTypes: currentTypes,
      });

      this.medicalVisitTypeForm.reset();
      return;
    }

    this.showMessage(
      'warn',
      this.translate.instant(
        'layout.configurations.runtimeSettings.errorMessage.title',
      ),
      this.translate.instant('layout.configurations.runtimeSettings.errors.duplicateMedicalVisitType', { value: option.code }),
    );

    return;
  }

  this.runtimeSettingsForm.patchValue({
    medicalVisitTypes: [...currentTypes, option],
  });

  this.medicalVisitTypeForm.reset();
  }
  removeMedicalVisitType(code: string): void {
  if (!this.canWrite) {
    return;
  }

  const updatedTypes = this.medicalVisitTypes.map((type) =>
    this.normalizeConfigCode(type.code) === this.normalizeConfigCode(code)
      ? {
          ...type,
          active: false,
        }
      : type,
  );

  this.runtimeSettingsForm.patchValue({
    medicalVisitTypes: updatedTypes,
  });
  }
  private buildLocalizedOption(value: any): LocalizedConfigOption {
  return {
    code: this.normalizeConfigCode(value.code),
    labelEn: value.labelEn?.trim(),
    labelEs: value.labelEs?.trim(),
    labelAr: value.labelAr?.trim(),
    active: true,
  };
}

private normalizeConfigCode(value: string): string {
  return (value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
}
  private findStringDuplicate(list: string[]): string | null {
    if (!list || list.length === 0) return null;

    const normalizedMap = new Map<string, string>();

    for (const item of list) {
      if (!item) continue;

      const trimmed = item.trim();
      if (trimmed === '') continue;

      const normalized = trimmed.toUpperCase();

      if (normalizedMap.has(normalized)) {
        return trimmed;
      }

      normalizedMap.set(normalized, trimmed);
    }

    return null;
  }

  private showMessage(severity: string, summary: string, detail: string) {
    this.messageService.add({ severity, summary, detail });
  }

  private handleError(error: any) {
    const message =
      error?.error?.message && typeof error.error.message === 'string'
        ? error.error.message
        : this.translations['layout.configurations.runtimeSettings.errorMessage.detail'];

    this.showMessage('error', this.translations['layout.configurations.runtimeSettings.errorMessage.title'], message);
  }
  private isRuntimeSettingsEqual(a: any, b: any): boolean {
    const ignoreKeys = ['id']; // ignore id field

    if (a === b) return true;
    if (!a || !b) return false;

    const keysA = Object.keys(a).filter((k) => !ignoreKeys.includes(k));
    const keysB = Object.keys(b).filter((k) => !ignoreKeys.includes(k));

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      const valA = a[key];
      const valB = b[key];

      if (Array.isArray(valA) && Array.isArray(valB)) {
        if (valA.length !== valB.length) return false;
        for (let i = 0; i < valA.length; i++) {
          if (!this.isRuntimeSettingsEqual(valA[i], valB[i])) return false;
        }
      } else if (valA && typeof valA === 'object' && valB && typeof valB === 'object') {
        if (!this.isRuntimeSettingsEqual(valA, valB)) return false;
      } else if (valA !== valB) {
        return false;
      }
    }

    return true;
  }
}
